import { errorMessages } from '@cascade/react/i18n';
import type { TErrorMessageKey } from '@cascade/react/i18n';
import { defaultLabels } from '@cascade/ui-library';
import type { TLabelKey } from '@cascade/ui-library';
import type { TAppMessageKey } from './types.ts';

export const enUS = {
  ...errorMessages,
  ...defaultLabels,
  'authentication.logo': 'Cascade',
  'authentication.usp':
    'The most advanced, trusted, and helpful AI for IT and HR Shared Services teams and employees',
  'session.signOut': 'Sign out',
  'support.prompt': 'Having trouble? Please contact us at {email}',
  'user.menu': '{name}, user menu',
  'settings.title': 'Settings',
  'settings.account.title': 'Account',
  'settings.tags.title': 'Personalization',
  'settings.project.title': 'Agent',
  'settings.account.name': 'Name',
  'settings.account.nameInvalid':
    'Your name must be between {min} and {max} characters.',
  'settings.account.email': 'Email',
  'settings.account.save': 'Save changes',
  'dataControls.title': 'Data Controls',
  'dataControls.view': 'Conversation view controls',
  'dataControls.table': 'Conversations',
  'dataControls.search': 'Search conversations',
  'dataControls.none': 'No conversations.',
  'dataControls.conversation': 'Conversation',
  'dataControls.started': 'Started',
  'dataControls.selectAll': 'Select all conversations',
  'dataControls.select': 'Select {title}',
  'dataControls.selected': '{count} selected',
  'dataControls.download': 'Download',
  'dataControls.downloaded': 'Conversations downloaded.',
  'dataControls.downloadFailed': 'The conversations could not be downloaded.',
  'dataControls.delete': 'Delete',
  'dataControls.delete.title': 'Delete conversations',
  'dataControls.delete.description':
    'The {count} selected conversations will be deleted. This cannot be undone.',
  'dataControls.delete.cancel': 'Cancel',
  'dataControls.delete.confirm': 'Delete conversations',
  'dataControls.deleted': 'Conversations deleted.',
  'dataControls.deleteFailed': 'The conversations could not be deleted.',
  'dependents.title': 'Dependents',
  'dependents.table': 'Dependents',
  'dependents.none': 'No dependents yet.',
  'dependents.name': 'Name',
  'dependents.email': 'Email',
  'dependents.status': 'Status',
  'dependents.unnamed': '—',
  'dependents.status.active': 'Active',
  'dependents.status.pending': 'Invitation sent',
  'dependents.add': 'Add dependent',
  'dependents.add.title': 'Add a dependent',
  'dependents.add.description': 'They will get an email invitation to join.',
  'dependents.add.name': 'Name',
  'dependents.add.email': 'Email',
  'dependents.add.emailInvalid': 'Enter a valid email address.',
  'dependents.add.duplicate': 'This person is already one of your dependents.',
  'dependents.add.self': 'You cannot add yourself as a dependent.',
  'dependents.add.limit':
    'You can have up to three dependents. Remove one to add another.',
  'dependents.add.cancel': 'Cancel',
  'dependents.add.submit': 'Send invitation',
  'dependents.added':
    'Invitation sent to {email}. It may land in their spam folder.',
  'dependents.remove': 'Remove {name}',
  'dependents.remove.title': 'Remove dependent',
  'dependents.remove.description':
    '{name} will no longer be one of your dependents.',
  'dependents.remove.cancel': 'Cancel',
  'dependents.remove.confirm': 'Remove',
  'dependents.removed': 'Dependent removed.',
  'dependents.removeFailed': 'The dependent could not be removed.',
  'brand.poweredBy': 'Powered by',
  'legal.privacy': 'Privacy Policy',
  'legal.terms': 'Terms of Service',
  'legal.copyright': '© {year} Cascade - All Rights Reserved',
  'signIn.title': 'Sign in to your account',
  'signIn.email': 'Work email',
  'signIn.emailPlaceholder': 'name@company.com',
  'signIn.emailInvalid': 'Please enter a valid email address.',
  'signIn.submit': 'Sign In',
  'signIn.emailCheck.title': 'Just one more email check!',
  'signIn.emailCheck.subtitle':
    'For security reasons, please re-enter the email address you used to get the sign in link.',
  'signIn.magicLink.sent':
    'A sign in email has been sent to {email} and should arrive within a few minutes.',
  'signIn.magicLink.checkSpam':
    'Please be sure to check your spam, junk or promotions folder.',
  'signIn.magicLink.expiry':
    'For your security, requesting a new link will expire the one we just sent.',
  'signIn.magicLink.resend': 'Request a new link',
  'signIn.magicLink.resendIn': 'Request a new link ({seconds}s)',
  'signIn.emailNotFound.notFound':
    "We're sorry, we couldn't find your email address in our database.",
  'signIn.emailNotFound.contactEmployer':
    'Please contact your employer to ensure you have been registered for access to Cascade.',
  'signIn.emailNotFound.contactSupport':
    'If you believe this is an error, you can contact support using the link below for further assistance.',
  'signIn.emailNotFound.back': 'Go back',
  'navigation.title': 'Navigation',
  'navigation.toggle': 'Toggle navigation',
  'navigation.close': 'Close navigation',
  'navigation.newChat': 'Start new chat',
  'navigation.conversations': 'AI Agent Conversations',
  'experiences.title': 'Experiences',
  'experiences.guided': 'Guided experience',
  'experiences.recommended': 'Recommended',
  'experience.failed.title': 'This experience could not be opened',
  'experience.failed.noSteps': 'It has no steps to start on yet.',
  'experience.home': 'Back to home',
  'experience.consent.prompt': 'Please read and accept this before you start.',
  'experience.consent.accept': 'Accept and continue',
  'experience.back': 'Go back',
  'experience.next': 'Next step',
  'experience.complete': 'Complete experience',
  'experience.step.failed': 'We could not load this step.',
  'experience.step.retry': 'Try again',
  'experience.section.expand': 'Show more',
  'experience.section.collapse': 'Show less',
  'experience.help': 'Help me understand this',
  'experience.content.generating': 'Creating your personalized content',
  'experience.content.failed.badge': 'Generation failed',
  'experience.content.failed.title':
    'We could not generate your personalized content',
  'experience.content.failed.description':
    'Your answers are saved. You can try again now or continue and come back to this later.',
  'experience.content.retry': 'Try again',
  'experience.widget.answer': 'Your answer',
  'experience.option.recommended': '{title} (Recommended)',
  'experience.option.selected': 'Selected',
  'experience.chat.title': 'Experience Agent',
  'experience.chat.greeting':
    "Hi, I'm your agent for this experience. How can I help you today?",
  'experience.chat.open': 'Open the chat',
  'experience.chat.collapse': 'Collapse the chat',
  'experience.chat.unread': '{count} new',
  'experience.number.invalid': 'Enter a number.',
  'experience.number.between': 'Enter a number from {min} to {max}.',
  'experience.number.atLeast': 'Enter a number of {min} or more.',
  'experience.number.atMost': 'Enter a number of {max} or less.',
  'experience.summary.title': 'Thank you!',
  'experience.summary.completed': 'You have completed {title}.',
  'experience.summary.saved':
    'Your answers are saved. You can come back and review them at any time.',
  'experience.summary.keepCopy':
    'Your answers are saved. Keep a copy for your records or to share.',
  'experience.summary.review': 'Review my answers',
  'experience.summary.download': 'Download',
  'experience.summary.download.title': 'Download summary',
  'experience.summary.download.description':
    'Save a copy of your summary to your device.',
  'experience.summary.download.pending': 'Preparing summary',
  'experience.summary.email': 'Send via email',
  'experience.summary.email.title': 'Send to {email}',
  'experience.summary.email.description':
    'Get a link to your summary in your inbox.',
  'experience.summary.email.pending': 'Sending',
  'experience.summary.email.sent': 'Email sent',
  'experience.summary.retry': 'Try again',
  'experience.summary.sessionExpired':
    'Your session has expired. Please go through the experience again.',
  'experience.summary.empty': 'There is nothing to summarize yet.',
  'experience.summary.emailFailed':
    'We could not email your summary. Please try again.',
  'experience.summary.notFound': 'This summary is no longer available.',
  'experiences.description':
    'Guided walkthroughs that take you step by step through what you need.',
  'terms.title': 'Terms of Use',
  'terms.updated': 'Last updated: {date}',
  'terms.accept': 'Accept',
  'terms.decline': 'Decline',
  'terms.retry': 'Try again',
  'terms.missing': 'The terms of this AI agent are not available yet.',
  'terms.notRecorded':
    'Your acceptance could not be recorded. Please try again.',
  'signIn.termsDeclined.title': 'We are sorry to see you go!',
  'signIn.termsDeclined.declined':
    'You have chosen not to accept our terms and conditions.',
  'signIn.termsDeclined.support':
    'If you have any questions or need further assistance, please contact our support team.',
  'signIn.termsDeclined.thanks': 'Thank you for considering our application.',
  'signIn.termsDeclined.back': 'Back to sign in',
  'projects.failed.title': 'Your agents could not be loaded',
  'projects.failed.retry': 'Try again',
  'projects.none.title': 'No agents assigned',
  'projects.none.unassigned':
    'Your account is not assigned to any AI agent yet.',
  'projects.none.contactEmployer':
    'Please contact your employer to ensure you have been registered for access to Cascade.',
  'projects.none.contactSupport':
    'If you believe this is an error, you can contact support using the link below for further assistance.',
  'projects.select.title': 'Select your AI Agent',
  'projects.picker.label': 'AI agent',
  'projects.picker.placeholder': 'Select an agent',
  'projects.picker.confirm': 'Select agent',
  'tags.required.title': 'Personalize your answers',
  'tags.required.subtitle':
    'A few things about you help the agent give answers that fit your situation.',
  'tags.form.placeholder': 'Select {tag}',
  'tags.form.locked':
    'Some of the required information can only be set by your employer. Please contact them to complete your registration.',
  'tags.form.save': 'Save',
  'pending.session': 'Checking your session',
  'pending.signIn': 'Signing you in',
  'pending.signOut': 'Signing you out',
  'pending.projects': 'Loading your agents',
  'pending.terms': 'Loading the terms',
  'pending.tags': 'Loading your details',
  'pending.messages': 'Loading the conversation',
  'agent.name': 'Agent',
  'agent.attach': 'Attach files',
  'agent.attach.deleteFailed': 'We could not delete {filename}',
  'agent.intro.description': 'I will be your Agent, how can I help you today?',
  'agent.waiting.cascading': 'Cascading...',
  'agent.waiting.analyzing': 'Analyzing your question...',
  'agent.waiting.thinking': 'Thinking about this...',
  'agent.waiting.looking': 'Looking into it...',
  'agent.experience.detected':
    'Here is a guided experience that may help you with this topic:',
  'agent.incomplete':
    'This response was interrupted and may be incomplete. Try sending a new message.',
  'agent.disclaimer':
    'AI Agents can make mistakes. Verify important information.',
  'agent.reply.like': 'Good answer',
  'agent.reply.dislike': 'Poor answer',
  'agent.reply.copy': 'Copy',
  'agent.reply.copied': 'Copied to your clipboard',
  'agent.reply.copyFailed': 'The answer could not be copied',
  'agent.reply.liked': 'Liked!',
  'agent.reply.disliked': 'Disliked!',
  'agent.feedback.like.title': 'What went well?',
  'agent.feedback.like.description':
    'Tell us what made this answer helpful so we can do more of it.',
  'agent.feedback.dislike.title': 'What went wrong?',
  'agent.feedback.dislike.description':
    'Tell us what was missing or wrong so we can improve.',
  'agent.feedback.reason': 'Your feedback',
  'agent.feedback.placeholder': 'The answer was...',
  'agent.feedback.submit': 'Send feedback',
  'agent.feedback.cancel': 'Cancel',
  'agent.sensitiveInformation.title': 'Sensitive information detected',
  'agent.sensitiveInformation.description':
    "We've found some sensitive information in your message. Are you sure you want to share this information?",
  'agent.sensitiveInformation.notice':
    'If you choose to continue, your message will be sent as-is. Otherwise, you can go back and edit your message before sending.',
  'agent.sensitiveInformation.confirm': 'Continue',
  'agent.sensitiveInformation.cancel': 'Dismiss message',
  'install.title': 'Install Cascade',
  'install.description': 'Add to your home screen for quick, app-like access.',
  'install.action': 'Install',
  'install.dismiss': 'Dismiss',
  'install.guide.title': 'Add Cascade to your Home Screen',
  'install.guide.share': 'Tap the Share button in your browser.',
  'install.guide.add': "Choose 'Add to Home Screen'.",
  'install.guide.confirm': "Tap 'Add' to finish installing Cascade.",
  'errorPage.title': 'Something went wrong',
  'errorPage.retry': 'Try again',
  'failure.home': 'Back to home',
  'failure.retry': 'Try again',
  'pending.tickets': 'Checking your tickets',
  'pending.ticket': 'Loading the ticket',
  'pending.experiences': 'Loading your experiences',
  'pending.experience': 'Opening the experience',
  'pending.step': 'Loading the step',
  'tickets.title': 'Tickets',
  'tickets.none': 'No tickets available.',
  'tickets.pages': 'Ticket pages',
  'tickets.failed.title': 'Your tickets could not be checked',
  'tickets.failed.retry': 'Try again',
  'tickets.unread': '{count} new',
  'tickets.updatedAt': 'Updated {date}',
  'tickets.status.open': 'Open',
  'tickets.status.pending': 'Pending',
  'tickets.status.inProgress': 'In progress',
  'tickets.status.reopened': 'Reopened',
  'tickets.status.closed': 'Closed',
  'ticket.breadcrumbs': 'Ticket',
  'ticket.thread': 'Ticket thread',
  'ticket.conversationFailed':
    'The conversation this ticket came from could not be loaded.',
  'ticket.support': 'Support',
  'ticket.you': 'You',
  'ticket.reply': 'Write a reply',
  'ticket.replyTooShort': 'Messages must be at least 3 characters.',
  'ticket.replyFailed': 'The reply could not be sent.',
  'ticket.updateFailed': 'The ticket could not be updated.',
  'ticket.resolve': 'Mark ticket as resolved',
  'ticket.resolve.title': 'Reason for resolving',
  'ticket.resolve.description':
    'Please include any additional notes regarding your reasons for resolving this ticket.',
  'ticket.resolve.submit': 'Resolve ticket',
  'ticket.reopen': 'Reopen ticket',
  'ticket.reopen.title': 'Reason for reopening',
  'ticket.reopen.description':
    'Please include any additional notes regarding your reasons for reopening this ticket.',
  'ticket.reopen.submit': 'Reopen ticket',
  'ticket.reason.label': 'Reason',
  'ticket.reason.placeholder': 'Start typing your reason...',
  'ticket.reason.cancel': 'Cancel',
  'ticket.change.opened': '{actor} opened this ticket',
  'ticket.change.pending': '{actor} set this ticket to pending',
  'ticket.change.inProgress': '{actor} marked this ticket as in progress',
  'ticket.change.reopened': '{actor} reopened this ticket',
  'ticket.change.resolved': '{actor} resolved this ticket',
  'ticket.change.assigned': '{actor} updated the ticket assignment',
  'ticket.change.priority': '{actor} changed the ticket priority',
  'ticket.change.updated': '{actor} updated this ticket',
  'ticket.create': 'Create ticket',
  'ticket.create.title': 'Create a ticket',
  'ticket.create.done': 'All set!',
  'ticket.create.destination': 'Send to',
  'ticket.create.destinationPlaceholder': 'Select a destination',
  'ticket.create.destinationsFailed':
    'The destinations could not be loaded. Close this dialog and try again.',
  'ticket.create.info':
    'We will create a ticket with your chat history so that you can continue the conversation where you left off.',
  'ticket.create.privacy':
    'Your conversations in Cascade stay confidential. The chat history of this conversation will be shared with the team handling your ticket.',
  'ticket.create.instructions':
    'Add any notes you would like the team handling your ticket to review along with the chat history.',
  'ticket.create.description': 'Description',
  'ticket.create.descriptionPlaceholder': 'Start typing',
  'ticket.create.history': 'Chat history',
  'ticket.create.historyEmpty': 'No messages yet.',
  'ticket.create.confirmation':
    'The team handling your ticket will reach back to you at the email address below.',
  'ticket.create.email':
    'Your email address attached to the ticket is {email}.',
  'ticket.create.view': 'View ticket {displayId}',
  'ticket.create.continue': 'Continue',
  'ticket.create.back': 'Go back',
  'ticket.create.submit': 'Submit',
  'ticket.create.cancel': 'Cancel',
  'ticket.create.close': 'Close',
  'ticket.create.failed': 'The ticket could not be created.',
  'ticket.create.length': '{length}/{max}',
  'ticket.create.noOptions': 'No options available',
} as const satisfies Record<
  TAppMessageKey | TErrorMessageKey | TLabelKey,
  string
>;
